import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "loopai_billing_currency";

/**
 * Shared hook for international Razorpay pricing.
 *
 * - Auto-detects currency from the backend (`/api/billing/currency`) using
 *   Cloudflare / CloudFront country headers and Accept-Language.
 * - Persists a user-selected override in localStorage so the choice sticks
 *   across pages and reloads.
 * - Exposes `convertFromInr(amount)` which converts an INR amount into the
 *   currently selected currency using the server-provided FX rate.
 */
export const useCurrency = () => {
  const [currency, setCurrencyState] = useState("INR");
  const [symbol, setSymbol] = useState("₹");
  const [minorUnit, setMinorUnit] = useState(2);
  const [rate, setRate] = useState(1);
  const [supported, setSupported] = useState([]);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyCurrency = useCallback((payload, requested) => {
    setCurrencyState(payload.currency);
    setSymbol(payload.symbol);
    setMinorUnit(payload.minor_unit);
    setRate(payload.rate_from_inr);
    if (payload.supported) setSupported(payload.supported);
    if (payload.detected_country !== undefined) setDetectedCountry(payload.detected_country);
    // Only persist explicit overrides so auto-detected currency can update later.
    if (requested) {
      try {
        localStorage.setItem(STORAGE_KEY, payload.currency);
      } catch (_) {}
    }
  }, []);

  const fetchCurrency = useCallback(async (override) => {
    try {
      setLoading(true);
      const url = override
        ? `${BACKEND_URL}/api/billing/currency?override=${encodeURIComponent(override)}`
        : `${BACKEND_URL}/api/billing/currency`;
      const { data } = await axios.get(url);
      applyCurrency(data, override);
    } catch (err) {
      console.error("Currency detect failed, falling back to INR:", err);
    } finally {
      setLoading(false);
    }
  }, [applyCurrency]);

  useEffect(() => {
    let override = null;
    try {
      override = localStorage.getItem(STORAGE_KEY);
    } catch (_) {}
    fetchCurrency(override);
  }, [fetchCurrency]);

  const setCurrency = useCallback((code) => {
    if (!code) return;
    fetchCurrency(code);
  }, [fetchCurrency]);

  const formatAmount = useCallback((amount) => {
    if (amount === 0 || amount === null || amount === undefined) return `${symbol}0`;
    const rounded = minorUnit === 0
      ? Math.round(amount)
      : Math.round(amount * 100) / 100;
    const formatted = minorUnit === 0
      ? rounded.toLocaleString()
      : rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol}${formatted}`;
  }, [symbol, minorUnit]);

  const convertFromInr = useCallback((inrAmount) => {
    if (!inrAmount) return 0;
    if (currency === "INR") return inrAmount;
    const raw = inrAmount * rate;
    if (minorUnit === 0) return Math.round(raw / 10) * 10;
    return Math.round(raw * 100) / 100;
  }, [currency, rate, minorUnit]);

  const formatFromInr = useCallback((inrAmount) => {
    return formatAmount(convertFromInr(inrAmount));
  }, [formatAmount, convertFromInr]);

  return {
    currency,
    symbol,
    minorUnit,
    rate,
    supported,
    detectedCountry,
    loading,
    setCurrency,
    formatAmount,
    convertFromInr,
    formatFromInr,
  };
};

export default useCurrency;
